import React, { memo } from 'react';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';

import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  InputProps as MuiInputProps,
  styled,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

// ---------------------------------------------------------------------------
// Shared styled primitive
// ---------------------------------------------------------------------------

const StyledTextField = styled(MuiTextField)(({ disabled }) => ({
  backgroundColor: disabled ? '#F2F2F2' : 'white',
  '& .MuiOutlinedInput-root fieldset': {
    borderRadius: '2px',
  },
}));

// ---------------------------------------------------------------------------
// Fade-in wrapper (extracted so the keyframes aren't re-declared each render)
// ---------------------------------------------------------------------------

const FadeInBox = styled(Box)({
  animation: 'fadeIn 0.4s ease 0.02s both',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
});

// ---------------------------------------------------------------------------
// TextField – lightweight labelled text field (no form control)
// ---------------------------------------------------------------------------

interface TextFieldProps extends Omit<MuiTextFieldProps, 'label'> {
  label?: string;
  width?: string;
  step?: string;
  pattern?: string;
}

export const TextField = memo(function TextField({ label, step, pattern, width = '100%', ...rest }: TextFieldProps) {
  const theme = useTheme();

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" width={width}>
      {label && <Typography variant="body2">{label}</Typography>}
      <StyledTextField
        {...rest}
        fullWidth
        inputProps={{
          style: {
            fontSize: '12px',
            padding: theme.spacing(1),
            paddingInline: theme.spacing(2),
          },
          step,
          pattern,
        }}
      />
    </Box>
  );
});

// ---------------------------------------------------------------------------
// LabelledTextField – form-connected variant (react-hook-form Controller)
// ---------------------------------------------------------------------------

interface LabelledTextFieldProps<T extends FieldValues = FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  width?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  disabled?: boolean;
  rules?: RegisterOptions<T>;
  type?: React.HTMLInputTypeAttribute;
  step?: string;
  pattern?: string;
  valueAsNumber?: boolean;
  multiline?: boolean;
  placeholder?: string;
  tooltip?: string;
  dataTestId?: string;
}

const LabelledTextField = memo(function LabelledTextField<T extends FieldValues>({
  name,
  control,
  label,
  width = '100%',
  startAdornment,
  endAdornment,
  disabled = false,
  rules,
  step,
  pattern,
  type = 'text',
  valueAsNumber = false,
  multiline = false,
  placeholder,
  tooltip,
  dataTestId,
}: LabelledTextFieldProps<T>) {
  const InputProps: Partial<MuiInputProps> = {
    readOnly: disabled,
    startAdornment,
    endAdornment,
    ...(multiline && { multiline: true, rows: 4 }),
  };

  return (
    <FadeInBox display="flex" flexDirection="column" alignItems="flex-start" width={width}>
      <Typography variant="body2">
        {label}
        {tooltip && (
          <Tooltip sx={{ pl: '2px' }} title={tooltip}>
            <InfoOutlineIcon fontSize="inherit" />
          </Tooltip>
        )}
      </Typography>

      <Controller
        name={name}
        control={control}
        rules={rules as RegisterOptions<T, Path<T>>}
        render={({ field, fieldState: { error } }) => (
          <StyledTextField
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              if (valueAsNumber) {
                const raw = e.target.value;
                field.onChange(raw === '' ? undefined : Number(raw));
              } else {
                field.onChange(e);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            placeholder={placeholder}
            type={type}
            size="small"
            fullWidth
            disabled={disabled}
            multiline={multiline}
            rows={multiline ? 4 : 1}
            InputProps={InputProps}
            error={!!error}
            helperText={error ? `*${error.message || error.type}` : undefined}
            inputProps={{ style: { fontSize: '12px' }, step, pattern, 'data-testid': dataTestId }}
          />
        )}
      />
    </FadeInBox>
  );
}) as <T extends FieldValues>(props: LabelledTextFieldProps<T>) => React.ReactElement;

export default LabelledTextField;
